import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Baby, Users, GraduationCap, HeartHandshake, Eye, Heart, Home, MessageCircle, RefreshCw, Sparkles, XCircle } from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

const PILLARS = [
  {
    n: "01",
    title: "Lo Stare",
    tagline: "Il cuore del metodo.",
    body: "Stare significa essere presenti davvero: con il corpo, con lo sguardo, con l'ascolto e con l'intenzione educativa. Non riempire di stimoli, ma osservare, attendere, accogliere e accompagnare. L'adulto diventa base emotiva stabile — sostiene senza invadere, guida senza controllare.",
  },
  {
    n: "02",
    title: "Il Benessere Globale",
    tagline: "Il bambino è parte di un sistema.",
    body: "Il bambino appartiene alla sua famiglia, alle sue relazioni, al suo ambiente. Ogni intervento educativo cerca equilibrio, sicurezza e sostenibilità nella quotidianità familiare — non solo per il bambino, ma per tutti.",
  },
  {
    n: "03",
    title: "Il Tempo",
    tagline: "Ogni bambino ha il suo ritmo.",
    body: "Sonno, alimentazione, movimento, autonomie, linguaggio, distacco, pannolino, ciuccio, relazione. Nessuna pressione, nessun confronto, nessuna aspettativa irrealistica. Il bambino non va accelerato: va accompagnato.",
  },
  {
    n: "04",
    title: "L'Ambiente Educante",
    tagline: "Gli spazi parlano.",
    body: "Un ambiente sicuro, accessibile, ordinato, autentico, pensato per il bambino. Non serve a intrattenere, ma a permettere esperienza, autonomia, scoperta e relazione.",
  },
  {
    n: "05",
    title: "Il Corpo e il Movimento",
    tagline: "Il primo linguaggio del bambino.",
    body: "Integriamo la psicomotricità relazionale di Bernard Aucouturier. Movimento libero, esplorazione spontanea, gioco corporeo, sicurezza affettiva, espressione emotiva. Il movimento è esperienza emotiva, relazionale e identitaria.",
  },
  {
    n: "06",
    title: "L'Osservazione",
    tagline: "Comprendere prima di intervenire.",
    body: "L'adulto osserva senza giudicare, senza etichettare, senza interpretazioni rigide. Solo così si costruiscono interventi davvero personalizzati e rispettosi dell'unicità di ogni famiglia.",
  },
  {
    n: "07",
    title: "L'Autonomia Relazionale",
    tagline: "Autonomia che nasce dalla sicurezza.",
    body: "Il bambino diventa autonomo quando si sente sicuro, accolto e competente. Autonomie graduali, reali, sostenute da fiducia, scelta, ambiente preparato e accompagnamento emotivo.",
  },
];

const VALUES = [
  "Cura", "Rispetto", "Ascolto", "Presenza", "Fiducia", "Relazione",
  "Autonomia", "Gentilezza", "Competenza", "Professionalità",
  "Collaborazione", "Formazione continua",
];

const ETHICS = [
  "Rispettare ogni bambino nella sua unicità.",
  "Tutelare il benessere psicofisico del bambino.",
  "Collaborare con la famiglia in un clima di fiducia.",
  "Mantenere la riservatezza.",
  "Formarsi continuamente.",
  "Lavorare senza giudizio.",
  "Riconoscere i propri limiti professionali e collaborare con altri specialisti quando necessario.",
  "Promuovere una cultura educativa basata sul rispetto, sulla gentilezza e sulla competenza.",
];

const OPERATIONAL_PRINCIPLES = [
  "Il bambino è parte attiva della relazione",
  "L'adulto accompagna senza sostituirsi",
  "L'osservazione precede l'intervento",
  "L'ambiente educa",
  "Le routine sono momenti relazionali",
  "Il benessere della famiglia influenza il benessere del bambino",
  "La cura educativa passa attraverso il corpo, il tempo e la presenza",
];

const NOT_TATA = [
  "Approcci coercitivi",
  "Addestramento emotivo",
  "Accelerazione delle autonomie fisiologiche",
  "Premi e punizioni come base relazionale",
  "Standardizzazione dei bambini",
  "Separazione del bambino dal contesto familiare",
  "Bambino come soggetto passivo",
  "Cura ridotta a prestazione",
];

const ACRONYM = [
  { letter: "T", word: "Tempo", body: "Rispettare i tempi del bambino, senza forzature, anticipazioni o aspettative irrealistiche." },
  { letter: "A", word: "Ascolto", body: "Osservare e ascoltare il bambino, la famiglia, il corpo, le emozioni e i bisogni reali." },
  { letter: "T", word: "Tatto", body: "Prendersi cura attraverso presenza, delicatezza, relazione, contatto e sensibilità educativa." },
  { letter: "A", word: "Autonomia", body: "Accompagnare il bambino verso autonomie reali, graduali e rispettose, sostenendo sicurezza e fiducia." },
];

const PHASES = [
  { n: "01", title: "Osservare", icon: Eye, intro: "Prima di intervenire, l'adulto osserva.", body: "Il bambino, il corpo, il movimento, le emozioni, le routine, l'ambiente, la relazione familiare. L'osservazione permette di comprendere senza giudicare." },
  { n: "02", title: "Accogliere", icon: Heart, intro: "Ogni famiglia arriva con una storia, una fatica, una sensibilità.", body: "Un'accoglienza rispettosa e non giudicante. Ascoltare, creare fiducia, costruire alleanza, comprendere i bisogni reali della famiglia." },
  { n: "03", title: "Preparare l'ambiente", icon: Home, intro: "L'ambiente è parte attiva della relazione educativa.", body: "Spazi accessibili, riduzione della sovrastimolazione, autonomia favorita, sicurezza e funzionalità, materiali coerenti con lo sviluppo." },
  { n: "04", title: "Entrare in relazione", icon: Users, intro: "Presenza, ascolto e continuità. Il concetto dello STARE.", body: "Rispettare i tempi del bambino. Usare voce, corpo e sguardo in modo coerente. Accompagnare senza invadere. Sostenere senza sostituirsi." },
  { n: "05", title: "Accompagnare le routine", icon: RefreshCw, intro: "Le routine quotidiane diventano momenti educativi.", body: "Sonno, pappa, cambio, gioco, distacco, autonomie, pannolino, ciuccio. Ogni gesto viene vissuto come cura educativa." },
  { n: "06", title: "Comunicare con la famiglia", icon: MessageCircle, intro: "Chiara, empatica, professionale, rispettosa.", body: "La famiglia viene accompagnata, non giudicata. Nessuna colpevolizzazione, nessuna imposizione, nessun linguaggio aggressivo, nessun terrorismo educativo." },
  { n: "07", title: "Restituire", icon: Sparkles, intro: "La restituzione educativa è parte integrante del lavoro.", body: "Osservazioni, cambiamenti, difficoltà, evoluzioni, bisogni emersi. Non per valutare il bambino, ma per costruire consapevolezza condivisa." },
];

const INFLUENCES = [
  "Pensiero Montessori",
  "Approccio Pikler",
  "Psicomotricità di Aucouturier",
  "Visione Reggio Children",
  "Educazione rispettosa e relazionale",
  "Neuroscienze dello sviluppo",
  "Osservazione neonatale e infantile",
];

const AUDIENCES = [
  { icon: Baby, title: "Bambini 0–6 anni", body: "Nei percorsi educativi, al sonno, allo svezzamento, alle autonomie." },
  { icon: HeartHandshake, title: "Genitori", body: "Consulenze personalizzate, accompagnamento nelle scelte quotidiane." },
  { icon: Users, title: "Tate e babysitter", body: "Corsi one to one, formazione professionale, restituzione educativa." },
  { icon: GraduationCap, title: "Educatori e professionisti", body: "Formazione, aggiornamento e sguardo condiviso sull'infanzia." },
];

export default function MetodoTata() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 30]);

  return (
    <div data-testid="metodo-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[85vh] pt-32 md:pt-40 pb-16 px-6 md:px-10 overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Il metodo educativo di Teresa Vilardo</p>
          <h1 className="font-display text-[18vw] md:text-[14vw] lg:text-[18rem] leading-[0.82] text-[#3D2140]">
            <RevealText text={["Metodo", <span key="tata" className="text-[#FF2768]">T.A.T.A.</span>]} stagger={0.15} />
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8">
            <p className="md:col-span-6 md:col-start-4 font-editorial text-2xl md:text-3xl text-[#3D2140] leading-snug italic">
              "Un approccio educativo relazionale e rispettoso che accompagna il bambino e la sua famiglia attraverso presenza, osservazione, ambiente e cura autentica."
            </p>
          </div>
          <p className="mt-8 md:col-span-5 md:col-start-4 text-sm uppercase tracking-[0.35em] text-[#81C3C9]">
            all'interno di T.A.T.A. Academy
          </p>
          <div className="mt-10 md:col-span-6 md:col-start-4 flex flex-wrap gap-3" data-testid="anchor-nav">
            <a href="#metodo" className="text-[10px] uppercase tracking-[0.4em] px-4 py-2 rounded-full bg-[#3D2140] text-white font-semibold hover:bg-[#FF2768] transition-colors">01 — Il Metodo</a>
            <a href="#protocollo" className="text-[10px] uppercase tracking-[0.4em] px-4 py-2 rounded-full border border-[#3D2140]/30 text-[#3D2140] font-semibold hover:border-[#FF2768] hover:text-[#FF2768] transition-colors">02 — Il Protocollo</a>
          </div>
        </div>

        <motion.img
          src={BRAND.logo}
          alt=""
          aria-hidden
          style={{ y: bearY, rotate: bearRotate }}
          className="absolute right-4 md:right-16 top-24 md:top-40 h-32 md:h-56 w-32 md:w-56 rounded-full opacity-80"
        />
      </section>

      {/* INTRO */}
      <section id="metodo" className="px-6 md:px-10 py-20 md:py-28 bg-[#81C3C9]/25 scroll-mt-24" data-testid="intro-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Da dove nasce</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              23 anni<br/>di sguardi,<br/><span className="text-[#FF2768]">di gesti,<br/>di famiglie.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-5 text-lg text-[#3D2140]/85 leading-relaxed">
            <p>Il Metodo T.A.T.A. nasce dall'esperienza educativa, relazionale e familiare maturata da Teresa Vilardo in <strong>oltre 23 anni di lavoro</strong> accanto ai bambini e alle loro famiglie.</p>
            <p>Un metodo che unisce cura, osservazione, presenza autentica e competenze educative, mettendo al centro il <strong>benessere globale del bambino</strong> e dell'intero sistema familiare.</p>
            <p className="font-editorial text-2xl text-[#3D2140] italic pt-2">Non insegnare più velocemente. Accompagnare il bambino nella sua unicità.</p>
          </div>
        </div>
      </section>

      {/* MISSIONE & VISIONE */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="mission-vision-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Missione</p>
            <h3 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9] mb-6">
              Una cultura<br/>della <span className="text-[#FF2768]">cura educativa.</span>
            </h3>
            <p className="text-[#3D2140]/85 text-lg leading-relaxed">
              Il Metodo T.A.T.A. nasce per promuovere una cultura della cura educativa, fondata sul rispetto del bambino, sul sostegno alle famiglie e sulla valorizzazione dei professionisti dell'infanzia. Costruire relazioni autentiche che favoriscano il benessere, l'autonomia e uno sviluppo armonico nei primi anni di vita.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:mt-24"
          >
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#81C3C9] mb-4">— Visione</p>
            <h3 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9] mb-6">
              Adulti preparati.<br/>Bambini <span className="text-[#81C3C9]">ascoltati.</span>
            </h3>
            <p className="text-[#3D2140]/85 text-lg leading-relaxed">
              Immagino un futuro in cui ogni bambino possa crescere accanto ad adulti preparati, consapevoli e rispettosi dei suoi tempi. In cui la figura della tata professionista e dell'educatore venga riconosciuta come una risorsa educativa fondamentale per la società.
            </p>
          </motion.div>
        </div>
      </section>

      {/* VALORI */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#81C3C9]/20 overflow-hidden" data-testid="values-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-16 max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Valori</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Le parole<br/>che <span className="text-[#FF2768]">guidano.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-4 md:gap-x-5 md:gap-y-6">
            {VALUES.map((v, i) => (
              <motion.span
                key={v}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#3D2140] px-4 py-2 hover:text-[#FF2768] transition-colors cursor-default leading-none"
                data-testid={`value-${i}`}
              >
                {v}<span className="text-[#FF2768]">.</span>
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* CODICE ETICO */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="ethics-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Codice etico</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Chi applica<br/>il Metodo T.A.T.A.<br/><span className="text-[#FF2768]">si impegna a…</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end">
            <p className="text-lg text-[#3D2140]/70 leading-relaxed font-editorial italic">
              Non una lista di regole. Una promessa condivisa che protegge il bambino, la famiglia e la professione.
            </p>
          </div>
        </div>

        <ol className="space-y-0 border-y border-[#3D2140]/15">
          {ETHICS.map((e, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="grid grid-cols-12 gap-4 md:gap-8 items-baseline py-6 md:py-8 border-b border-[#3D2140]/15 last:border-b-0 group"
              data-testid={`ethic-${i}`}
            >
              <span className="col-span-2 md:col-span-1 font-editorial text-[#FF2768] text-lg md:text-2xl">0{i + 1}</span>
              <span className="col-span-10 md:col-span-11 text-lg md:text-2xl text-[#3D2140] leading-snug group-hover:text-[#FF2768] transition-colors">
                {e}
              </span>
            </motion.li>
          ))}
        </ol>
      </section>

      {/* 7 PILLARS */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="pillars-section">
        <div className="mb-20">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— I 7 pilastri</p>
          <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
            Sette parole.<br/>Un solo modo di stare.
          </h2>
        </div>

        <div className="space-y-24 md:space-y-32">
          {PILLARS.map((p, idx) => {
            const isEven = idx % 2 === 0;
            return (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start"
                data-testid={`pillar-${p.n}`}
              >
                <div className={`md:col-span-5 ${isEven ? "" : "md:order-2"}`}>
                  <div className="flex items-baseline gap-4 mb-6">
                    <span className="font-editorial text-[#FF2768] text-2xl">{p.n}</span>
                    <div className="h-px flex-1 bg-[#3D2140]/15" />
                  </div>
                  <h3 className="font-display text-6xl md:text-7xl text-[#3D2140] leading-none mb-3">{p.title}</h3>
                  <p className="font-editorial text-2xl text-[#FF2768] italic">{p.tagline}</p>
                </div>
                <div className={`md:col-span-6 ${isEven ? "md:col-start-7" : "md:col-start-1 md:order-1"}`}>
                  <p className="text-[#3D2140]/85 text-lg leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* INFLUENZE */}
      <section className="px-6 md:px-10 py-24 bg-[#FAF8F5]" data-testid="influenze-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Influenze pedagogiche</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Radici<br/>plurali.
            </h2>
            <p className="mt-6 text-[#3D2140]/70 leading-relaxed max-w-md">
              Approcci integrati in una visione educativa personale, umana e autentica. Nessuna rigidità: solo dialogo tra saperi diversi.
            </p>
          </div>
          <ul className="md:col-span-7 space-y-4">
            {INFLUENCES.map((inf, i) => (
              <motion.li
                key={inf}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="flex items-baseline gap-6 border-b border-[#3D2140]/15 py-4"
              >
                <span className="font-editorial text-[#FF2768] text-lg">0{i + 1}</span>
                <span className="font-display text-2xl md:text-3xl text-[#3D2140]">{inf}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* A CHI SI RIVOLGE */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="audiences-section">
        <div className="mb-16">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— A chi si rivolge</p>
          <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
            Per chi<br/>si prende cura.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="p-8 rounded-3xl border border-[#81C3C9]/40 bg-white hover:border-[#FF2768] transition-colors duration-500"
              >
                <span className="inline-flex h-14 w-14 rounded-2xl bg-[#FF2768] text-white items-center justify-center mb-6">
                  <Icon size={22} />
                </span>
                <h3 className="font-display text-3xl text-[#3D2140] mb-3">{a.title}</h3>
                <p className="text-[#3D2140]/70 leading-relaxed text-sm">{a.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* MODELLO EDUCATIVO T.A.T.A. */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#3D2140] text-white relative overflow-hidden" data-testid="modello-section">
        <div className="absolute -left-24 top-24 h-96 w-96 rounded-full bg-[#FF2768]/15 blur-3xl pointer-events-none" />
        <div className="max-w-[1600px] mx-auto relative">
          <div className="mb-16 md:mb-20 grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-6">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] mb-4">— Il sistema</p>
              <h2 className="font-display text-6xl md:text-8xl leading-[0.9]">
                Il Modello<br/><span className="text-[#FF2768]">Educativo T.A.T.A.®</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 self-end">
              <p className="text-white/70 leading-relaxed">
                Un ecosistema educativo strutturato: dalla filosofia agli strumenti, dalla formazione ai percorsi. Tutto nasce e converge in <strong className="text-white">T.A.T.A. Academy</strong>.
              </p>
            </div>
          </div>

          <div className="space-y-2 md:space-y-3">
            {[
              { n: "01", title: "T.A.T.A. Academy", role: "Il contenitore", link: "/academy" },
              { n: "02", title: "Modello Educativo T.A.T.A.®", role: "L'identità professionale" },
              { n: "03", title: "Metodo T.A.T.A.", role: "I valori e la filosofia" },
              { n: "04", title: "Protocollo T.A.T.A.", role: "L'applicazione pratica", link: "#protocollo" },
              { n: "05", title: "Strumenti T.A.T.A.", role: "I materiali operativi" },
              { n: "06", title: "Formazione T.A.T.A.", role: "I percorsi formativi" },
              { n: "07", title: "Valutazione T.A.T.A.", role: "Il ritorno educativo" },
              { n: "08", title: "Percorsi T.A.T.A.", role: "L'offerta e la formazione" },
            ].map((row, i) => {
              const content = (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className="group grid grid-cols-12 gap-4 items-baseline py-5 md:py-6 border-b border-white/10 hover:border-[#FF2768] transition-colors"
                >
                  <span className="col-span-2 md:col-span-1 font-editorial text-[#FFE45C] text-sm md:text-base">{row.n}</span>
                  <span className="col-span-6 md:col-span-6 font-display text-2xl sm:text-3xl md:text-5xl text-white group-hover:text-[#FF2768] transition-colors leading-none">{row.title}</span>
                  <span className="col-span-4 md:col-span-5 text-right text-xs md:text-sm uppercase tracking-widest text-white/50">{row.role}</span>
                </motion.div>
              );
              return row.link ? (row.link.startsWith("#") ? <a key={i} href={row.link} className="block">{content}</a> : <Link key={i} to={row.link} className="block">{content}</Link>) : <div key={i}>{content}</div>;
            })}
          </div>
        </div>
      </section>

      {/* LINK TO PROTOCOLLO */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#81C3C9]/25" data-testid="protocollo-link-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— L'applicazione pratica</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9] mb-6">
              Dal metodo,<br/>il <span className="text-[#FF2768]">Protocollo T.A.T.A.</span>
            </h2>
            <p className="text-lg text-[#3D2140]/80 leading-relaxed max-w-lg mb-8">
              Sette fasi operative pensate per tate, educatori e professionisti dell'infanzia. Il modo concreto in cui il Metodo T.A.T.A. prende vita nel lavoro quotidiano.
            </p>
            <Link to="/protocollo-tata" data-testid="cta-protocollo" className="btn-tv-primary">
              Scopri il Protocollo T.A.T.A. <ArrowRight size={16} />
            </Link>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex justify-end">
            <img src={BRAND.logo} alt="" aria-hidden className="h-48 md:h-64 w-48 md:w-64 rounded-full animate-floaty drop-shadow-xl" />
          </div>
        </div>
      </section>

      {/* PROTOCOLLO — DIVIDER */}
      <section id="protocollo" className="px-6 md:px-10 pt-24 md:pt-32 pb-8 max-w-[1600px] mx-auto scroll-mt-24" data-testid="protocollo-intro-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Dal metodo alla pratica</p>
            <h2 className="font-display text-6xl md:text-8xl lg:text-9xl text-[#3D2140] leading-[0.88]">
              Protocollo<br/><span className="text-[#FF2768]">T.A.T.A.®</span>
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-lg text-[#3D2140]/85 leading-relaxed font-editorial italic">
              "Il Protocollo T.A.T.A. traduce la cura educativa in presenza concreta, osservazione e relazione quotidiana."
            </p>
          </div>
        </div>
      </section>

      {/* ACRONIMO T-A-T-A */}
      <section className="px-6 md:px-10 py-16 md:py-24 max-w-[1600px] mx-auto" data-testid="acronym-section">
        <div className="mb-16">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— L'acronimo</p>
          <h3 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
            Quattro lettere.<br/>Quattro <span className="text-[#FF2768]">gesti di cura.</span>
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-16">
          {ACRONYM.map((a, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.7, delay: (idx % 2) * 0.1 }}
              className="flex items-start gap-6 md:gap-10"
            >
              <span className="font-display text-[9rem] md:text-[12rem] leading-[0.8] text-[#FF2768] shrink-0">{a.letter}</span>
              <div className="pt-4">
                <p className="text-[10px] uppercase tracking-[0.4em] text-[#81C3C9] font-semibold mb-2">— {a.letter} come</p>
                <h4 className="font-display text-5xl md:text-6xl text-[#3D2140] mb-4">{a.word}</h4>
                <p className="text-[#3D2140]/80 leading-relaxed text-lg">{a.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRINCIPI OPERATIVI */}
      <section className="px-6 md:px-10 py-24 bg-[#FAF8F5]" data-testid="principles-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Principi operativi</p>
            <h3 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Sette bussole<br/>per la <span className="text-[#FF2768]">quotidianità.</span>
            </h3>
          </div>
          <ul className="md:col-span-7 space-y-1">
            {OPERATIONAL_PRINCIPLES.map((p, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="flex items-baseline gap-6 border-b border-[#3D2140]/15 py-5 group"
              >
                <span className="font-editorial text-[#FF2768] text-lg shrink-0 w-8">0{i + 1}</span>
                <span className="font-display text-2xl md:text-3xl text-[#3D2140] group-hover:text-[#FF2768] transition-colors">{p}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* 7 FASI */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="phases-section">
        <div className="mb-20">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Le 7 fasi</p>
          <h3 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
            Sette passi.<br/>Un solo sguardo.
          </h3>
        </div>
        <div className="space-y-16 md:space-y-24">
          {PHASES.map((phase, idx) => {
            const Icon = phase.icon;
            const isEven = idx % 2 === 0;
            return (
              <motion.div
                key={phase.n}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start"
              >
                <div className={`md:col-span-4 ${isEven ? "" : "md:order-2"}`}>
                  <div className="flex items-baseline gap-4 mb-6">
                    <span className="font-editorial text-[#FF2768] text-2xl">{phase.n}</span>
                    <div className="h-px flex-1 bg-[#3D2140]/15" />
                  </div>
                  <span className="inline-flex h-14 w-14 rounded-2xl bg-[#FF2768] text-white items-center justify-center mb-4"><Icon size={22} /></span>
                  <h4 className="font-display text-5xl md:text-6xl text-[#3D2140] leading-none">{phase.title}</h4>
                </div>
                <div className={`md:col-span-7 ${isEven ? "md:col-start-6" : "md:col-start-1 md:order-1"} space-y-4`}>
                  <p className="font-editorial text-xl md:text-2xl text-[#3D2140] leading-snug italic">{phase.intro}</p>
                  <p className="text-[#3D2140]/80 text-lg leading-relaxed">{phase.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* COSA NON È */}
      <section className="relative px-6 md:px-10 py-24 md:py-32 bg-[#3D2140] text-white overflow-hidden" data-testid="not-tata-section">
        <div className="max-w-[1400px] mx-auto relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-7">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] mb-4">— Cosa NON è il Protocollo T.A.T.A.</p>
              <h3 className="font-display text-5xl md:text-7xl leading-[0.9]">
                Non nasce per<br/><span className="text-[#FF2768]">controllare.</span>
              </h3>
              <p className="mt-6 text-white/70 leading-relaxed max-w-md">Non nasce per standardizzare la crescita. È l'esatto opposto di certe scorciatoie.</p>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {NOT_TATA.map((n, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="flex items-center gap-3 border-t border-white/10 py-4"
              >
                <XCircle size={18} className="text-[#FF2768] shrink-0" />
                <span className="text-white/85 text-base md:text-lg">{n}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* IL CUORE */}
      <section className="relative px-6 md:px-10 py-24 md:py-32 bg-[#3D2140] text-white overflow-hidden">
        <img src={BRAND.logo} alt="" aria-hidden className="absolute -right-16 -bottom-16 h-96 w-96 opacity-15" />
        <div className="max-w-[1400px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] mb-6">— Il cuore del Metodo</p>
          <h2 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] mb-10">
            Non controllo.<br/><span className="text-[#FF2768]">Accompagnamento.</span>
          </h2>
          <div className="max-w-2xl space-y-6 text-lg md:text-xl leading-relaxed text-white/85 font-editorial">
            <p>Il Metodo T.A.T.A. non vuole creare bambini perfetti o famiglie perfette.</p>
            <p>Vuole creare <strong className="text-white">relazioni più consapevoli</strong>, bambini più ascoltati, adulti più presenti.</p>
            <p className="text-[#81C3C9]">Perché educare non significa controllare. Educare significa accompagnare.</p>
          </div>

          <div className="mt-14 flex flex-wrap gap-3">
            <Link to="/prenota" data-testid="metodo-cta-booking" className="btn-tv-primary">
              Prenota una consulenza <ArrowRight size={16} />
            </Link>
            <Link to="/cosa-posso-fare-per-te" data-testid="metodo-cta-services" className="inline-flex items-center gap-2 px-7 py-[14px] rounded-full border border-white/40 text-white hover:bg-white hover:text-[#3D2140] transition-all">
              Vedi i percorsi <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
